import React from "react";
import Box from "@material-ui/core/Box";
import Grid from "@material-ui/core/Grid";
import Typography from "@material-ui/core/Typography";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import TablePaginationActions from "@material-ui/core/TablePagination/TablePaginationActions";
import { IconButton, makeStyles, TablePagination } from "@material-ui/core";
import { Edit } from "@material-ui/icons";
import { Link } from "react-router-dom";
import {
  useTable,
  useRowSelect,
} from "react-table";
import { CustomCheckbox } from "../../../util";
import apiHelper from "../../../util/ApiHelper";
import { useLoading } from "../../../util/LoadingProvider";
import "./index.scss";

const useStyles = makeStyles({
  iconBtn: {
    color: "#CCCCCC",
    margin: 5,
  },
});

const IndeterminateCheckbox = React.forwardRef(
  ({ indeterminate, ...rest }, ref) => {
    const defaultRef = React.useRef();
    const resolvedRef = ref || defaultRef;

    React.useEffect(() => {
      resolvedRef.current.indeterminate = indeterminate;
    }, [resolvedRef, indeterminate]);

    return <CustomCheckbox type="checkbox" ref={resolvedRef} {...rest} />;
  }
);

function getKeyword(keyword) {
  return keyword ? `?keyword=${keyword}` : "";
}

function InvitedEntryListingPage(props) {
  const { match, disabled, location } = props;
  const classes = useStyles();
  const [keyword, setKeyword] = React.useState("");
  const [queryString, setQueryString] = React.useState("");
  const [data, setData] = React.useState([]);
  const { setLoading } = useLoading();

  // for pagination
  const [page, setPage] = React.useState(
    (location.state && location.state.page) || 0
  );
  const [rowsPerPage, setRowsPerPage] = React.useState(
    (location.state && location.state.rowsPerPage) || 10
  );
  const [totalElements, setTotalElements] = React.useState(0);

  const CustomLink = React.useMemo(
    () =>
      React.forwardRef((linkProps, ref) => <Link ref={ref} {...linkProps} />),
    []
  );

  const triggerSearch = React.useCallback(() => {
    setQueryString(keyword);
  }, [keyword]);

  const getInvitedEntries = React.useCallback(async () => {
    try {
      setLoading(true);
      const result = await apiHelper.hkiff.getInvitedEntries(
        null,
        page,
        (page + 1) * rowsPerPage,
        rowsPerPage,
        queryString
      );
      console.log("result:", result);
      setData(result.data.content.filter((x) => x));
      setTotalElements(result.data.totalElements);
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, queryString, setLoading]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    if (event.target.value > 0) {
      setRowsPerPage(parseInt(event.target.value, 10));
      setPage(0);
    } else {
      getInvitedEntries();
      setRowsPerPage(parseInt(event.target.value, 10));
      setPage(0);
    }
  };

  const columns = React.useMemo(
    () => [
      {
        Header: "Ref.#",
        accessor: "referenceNum",
        className: "col_referenceNum",
      },
      {
        Header: "Title (Traditional Chinese)",
        accessor: "titleTc",
        className: "col_originalTitle",
        Cell: ({ row, column, ...rest }) => {
          return (
            <Link
              to={{
                pathname: `${match.url}/${row.original.referenceNum}`,
                state: { page: page, rowsPerPage: rowsPerPage },
              }}
              className={column.className}
            >
              {row.original.titleTc}
            </Link>
          );
        },
      },
      {
        Header: "Title (English)",
        accessor: "titleEn",
        className: "col_englishTitle",
        Cell: ({ row, column, ...rest }) => {
          return (
            <Link
              to={{
                pathname: `${match.url}/${row.original.referenceNum}`,
                state: { page: page, rowsPerPage: rowsPerPage },
              }}
              className={column.className}
            >
              {row.original.titleEn}
            </Link>
          );
        },
      },
      {
        Header: "Year of Premiere",
        accessor: "yearOfPremiere",
      },
      {
        Header: "Production Country",
        accessor: "productionCountry",
      },
      {
        Header: "Duration",
        accessor: "duration",
      },
      {
        Header: "Screen Format",
        accessor: "screenFormat",
        className: "col_canSendEmail",
        disableGlobalFilter: true,
      },
      {
        Header: "Create Time",
        accessor: "createTime",
        className: "col_createTime",
      },
    ],
    [page, rowsPerPage]
  );

  const { getTableProps, headerGroups, rows, prepareRow } =
    useTable(
      {
        columns,
        data,
      },
      useRowSelect,
      (hooks) => {
        hooks.visibleColumns.push((columns) => [
          // Let's make a column for selection
          {
            id: "selection",
            // The header can use the table's getToggleAllRowsSelectedProps method
            // to render a checkbox
            Header: ({ getToggleAllRowsSelectedProps }) => (
              <div>
                <IndeterminateCheckbox
                  {...{ ...getToggleAllRowsSelectedProps(), disabled }}
                />
              </div>
            ),
            // The cell can use the individual row's getToggleRowSelectedProps method
            // to the render a checkbox
            Cell: ({ row }) => (
              <div>
                <IndeterminateCheckbox
                  {...{ ...row.getToggleRowSelectedProps(), disabled }}
                />
              </div>
            ),
          },
          ...columns,
          {
            id: "action",
            // The header can use the table's getToggleAllRowsSelectedProps method
            // to render a checkbox
            Header: () => <div>Action</div>,
            // The cell can use the individual row's getToggleRowSelectedProps method
            // to the render a checkbox
            Cell: ({ row }) => (
              <div style={{ display: "inline-flex" }}>
                <IconButton
                  className={classes.iconBtn}
                  size="small"
                  component={CustomLink}
                  to={{
                    pathname: `${match.url}/${row.original.referenceNum}`,
                    state: { page: page, rowsPerPage: rowsPerPage },
                  }}
                >
                  <Edit fontSize="small" />
                </IconButton>
              </div>
            ),
          },
        ]);
      }
    );

  React.useEffect(() => {
    getInvitedEntries();
  }, [getInvitedEntries]);

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Entry Management > Invited Entry
      </Typography>

      {/* Start of Search Bar */}
      <Grid container justify="flex-end">
        <Grid container item xs={12} md={8}>
          <Box
            width="100%"
            component="span"
            display="flex"
            justifyContent="flex-end"
            alignItems="center"
          >
            <Box display="flex">
              <Button
                variant="contained"
                color="primary"
                type="button"
                component={"a"}
                download
                href={`${
                  process.env.REACT_APP_API_HOSTNAME ||
                  "https://hkiff-api-dev.cloud.udev.hk"
                }/hkiff/admin/entries/invited-entry/excelExport${getKeyword(
                  queryString
                )}`}
              >
                Export Invited Entry
              </Button>
            </Box>
            <Box display="flex">
              <TextField
                id="outlined-error"
                label="Search"
                variant="outlined"
                margin="dense"
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
              />
            </Box>
            <Box display="flex">
              <Button
                variant="outlined"
                color="primary"
                type="button"
                margin="dense"
                onClick={triggerSearch}
              >
                Search
              </Button>
            </Box>
          </Box>
        </Grid>

        {/* Start of Table */}
        <Grid container xs={12} style={{ marginTop: "4px" }}>
          <Table aria-label="simple table" component="ul" {...getTableProps()}>
            <TableHead component="div">
              {headerGroups.map((headerGroup) => (
                <TableRow {...headerGroup.getHeaderGroupProps()}>
                  {headerGroup.headers.map((column) => (
                    <TableCell
                      {...column.getHeaderProps()}
                      className={`${column.className ?? ""}`}
                      style={{
                        paddingTop: 6,
                        paddingBottom: 6,
                        lineHeight: "1.1em",
                        height: "48px",
                      }}
                    >
                      {column.render("Header")}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableHead>
            <TableBody>
              {rows.map((row, index) => {
                prepareRow(row);
                return (
                  <TableRow {...row.getRowProps()}>
                    {row.cells.map((cell) => {
                      return (
                        <TableCell
                          {...cell.getCellProps()}
                          className={`${cell.column.className ?? ""}`}
                          style={{
                            paddingTop: 6,
                            paddingBottom: 6,
                            lineHeight: "1.1em",
                            height: "48px",
                          }}
                        >
                          {cell.render("Cell")}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Grid>
        <TablePagination
          component="div"
          rowsPerPageOptions={[5, 10, 25, { label: "All", value: 9999999 }]}
          colSpan={7}
          count={totalElements}
          rowsPerPage={rowsPerPage}
          page={page}
          SelectProps={{
            inputProps: { "aria-label": "rows per page" },
            native: true,
            style: {
              background: "white",
              border: "1px solid #CCCCCC",
              borderRadius: "4px",
            },
          }}
          onChangePage={handleChangePage}
          onChangeRowsPerPage={handleChangeRowsPerPage}
          ActionsComponent={TablePaginationActions}
          labelRowsPerPage="Show rows"
        />
      </Grid>
    </Box>
  );
}

export default InvitedEntryListingPage;
